import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { BrandCard } from '~/components/isolated/CategoryBlocks/shared/BrandCard';
import {
  GridView,
  tileClassNames,
} from '~/components/isolated/CategoryBlocks/shared/GridView';
import { IconButton } from '~/components/isolated/IconButton';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import type { Category } from '~/stores/CategoriesStore';

const MOBILE_TILES_LIMIT = 8;
const SLIDES_PER_VIEW = 4;

const CARD_TITLE_CLASS = 'w-[65%] [font:var(--header-50-medium)]';

const CARD_IMAGE_SIZES = '(min-width: 1025px) 288px, 50vw';

const CARD_IMAGE_ASPECT_RATIO = { mobile: 1, desktop: 288 / 200 };

const Body = ({
  items,
  priority = false,
}: {
  items: Category[];
  priority?: boolean;
}) => {
  const { isMobile } = useGlobal();

  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const children = items.map((category, index) => (
    <BrandCard
      key={category.id}
      category={category}
      imageSizes={CARD_IMAGE_SIZES}
      imageAspectRatio={CARD_IMAGE_ASPECT_RATIO}
      priority={priority && index === 0}
      classes={{
        titleClassName: CARD_TITLE_CLASS,
        tileClassName: tileClassNames[1],
      }}
    />
  ));

  if (isMobile) {
    return (
      <GridView className="grid-cols-2!">
        {children.slice(0, MOBILE_TILES_LIMIT)}
      </GridView>
    );
  }

  const carouselItems = children.map((child, index) => ({
    id: index,
    node: child,
  }));

  const showArrows = carouselItems.length > SLIDES_PER_VIEW;

  return (
    <div className="relative">
      <div className="carousel">
        {showArrows && (
          <IconButton
            icon="chevron-3.1"
            iconSize={14}
            size="XL"
            variant="shadowS"
            onClick={onPrevButtonClick}
            className={classes([
              'absolute top-1/2 left-[-28px] z-[2] -translate-y-1/2',
              prevBtnDisabled && 'hidden!',
            ])}
          />
        )}
        <Carousel
          opts={{
            align: 'start',
            slidesToScroll: SLIDES_PER_VIEW,
            containScroll: 'trimSnaps',
          }}
          setApi={setApi}
          className="h-50 w-full"
        >
          <CarouselContent>
            {carouselItems.map((item) => (
              <CarouselItem
                key={item.id}
                className="h-50 shrink-0 basis-72! pl-0! [&:not(:last-child)]:mr-4"
              >
                {item.node}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {showArrows && (
          <IconButton
            icon="chevron-3.1"
            iconSize={14}
            size="XL"
            variant="shadowS"
            onClick={onNextButtonClick}
            className={classes([
              'absolute top-1/2 right-[-28px] z-[2] -translate-y-1/2 [&>svg]:rotate-180',
              nextBtnDisabled && 'hidden!',
            ])}
          />
        )}
      </div>
    </div>
  );
};

export default observer(Body);
